import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { AxiosError } from 'axios'
import { fetchNpsFeatures, sendNpsRefusal, sendNpsReview } from '../api/npsApi'
import type {
  NpsFeaturesResponse,
  NpsRefusalRequest,
  NpsRefusalResponse,
  NpsReviewRequest,
  NpsReviewResponse,
} from '../types'

export const NPS_FEATURES_KEY = ['nps', 'features'] as const

/**
 * Список фич, которые можно оценить.
 *
 * Запрашивается один раз за сессию: список меняется только в ответ на действия
 * самого пользователя, новых фич по ходу сессии не появится. Пустой массив —
 * штатное и самое частое состояние, а не ошибка.
 */
export const useNpsFeatures = (enabled: boolean) =>
  useQuery<NpsFeaturesResponse, AxiosError>(NPS_FEATURES_KEY, fetchNpsFeatures, {
    enabled,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  })

/** Убирает фичу из уже полученного списка — перезапрашивать его не нужно. */
const useDropFeature = () => {
  const queryClient = useQueryClient()
  return (slug: string) =>
    queryClient.setQueryData<NpsFeaturesResponse>(NPS_FEATURES_KEY, current => ({
      features: (current?.features ?? []).filter(feature => feature.slug !== slug),
    }))
}

/**
 * Отправка отзыва.
 *
 * `409` и `404` считаем состоявшимся результатом, а не ошибкой: первый значит
 * «отзыв уже записан» (например, ответ на прошлую попытку не дошёл), второй —
 * «фичу выключили». В обоих случаях повторять бесполезно, а фичу надо убрать с
 * экрана — ровно то же, что и при успехе.
 */
export const useNpsReview = () => {
  const dropFeature = useDropFeature()

  return useMutation<NpsReviewResponse, AxiosError, NpsReviewRequest>(
    sendNpsReview,
    {
      retry: false,
      onSuccess: (_data, variables) => {
        dropFeature(variables.slug)
      },
      onError: (error, variables) => {
        const status = error.response?.status
        if (status === 409 || status === 404) dropFeature(variables.slug)
      },
    }
  )
}

/**
 * Отказ «не сейчас». Метод идемпотентен, поэтому двойное нажатие и ретрай
 * после таймаута безопасны. Срок, на который фича спрячется, задаёт бэк.
 */
export const useNpsRefuse = () => {
  const dropFeature = useDropFeature()

  return useMutation<NpsRefusalResponse, AxiosError, NpsRefusalRequest>(
    sendNpsRefusal,
    {
      retry: false,
      onSuccess: (_data, variables) => {
        dropFeature(variables.slug)
      },
      onError: (error, variables) => {
        if (error.response?.status === 404) dropFeature(variables.slug)
      },
    }
  )
}
